import React from 'react';
import Loadable from 'react-loadable';
import {
  AccessibilityAuditComponentResultsSection,
  ComponentPreview,
  DontDo,
  Link,
  PageHero,
  PageNavigation,
  PageWithSubNav,
  Paragraph,
  PlatformTable,
  Section,
  SectionSubhead,
} from '../../../components';
import V5Notice from '../v5-notice/V5Notice';
import selectMarkPreviewData from '../../../data/previews/select-mark.yml';
import pageHeroData from '../../../data/pages/components.yml';

const SelectMark = Loadable({
  loader: () => import('@hudl/uniform-ui-components'),
  loading: () => null,
  render(loaded, props) {
    const Component = loaded.SelectMark;
    return <Component {...props} />;
  },
});

export default class IndexPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sizeSelectMarkState: 'default',
      stateSelectMarkState: 'default',
    };
  }

  render() {
    return (
      <PageWithSubNav
        pageType="design"
        title="Select Mark"
        designCode
        subnav="components">
        <PageHero heroData={pageHeroData} tierTwo="Select Mark" />
        <V5Notice
          componentName="Select Mark"
          storybookUrl="https://uniform-web.storybook.hudltools.com/?path=/docs/general-select-mark--docs"
        />

        <PageNavigation
          links={['Size', 'State', 'Usage', 'Accessibility', 'Platform']}
        />

        <Section title="Size">
          <Paragraph>
            Select mark is available in small, medium and large. Consider view
            density and the mark’s position when choosing between the three.
          </Paragraph>
          <ComponentPreview
            name="SelectMarkSize"
            layout="default"
            previewData={selectMarkPreviewData.sizes}>
            <SelectMark
              onClick={() =>
                this.setState({
                  sizeSelectMarkState:
                    this.state.sizeSelectMarkState === 'default' ||
                    this.state.sizeSelectMarkState === 'unselected'
                      ? 'selected'
                      : 'unselected',
                })
              }
              selectedState={this.state.sizeSelectMarkState}
            />
          </ComponentPreview>
        </Section>

        <Section title="State">
          <Paragraph>
            The select mark’s state changes with mouse and touch interactions.
          </Paragraph>
          <ComponentPreview name="SelectMarkState" layout="default">
            <SelectMark
              onClick={() =>
                this.setState({
                  stateSelectMarkState:
                    this.state.stateSelectMarkState === 'default' ||
                    this.state.stateSelectMarkState === 'unselected'
                      ? 'selected'
                      : 'unselected',
                })
              }
              selectedState={this.state.stateSelectMarkState}
            />
          </ComponentPreview>
        </Section>

        <Section title="Usage">
          <SectionSubhead>Multi-Selection Mode</SectionSubhead>
          <Paragraph>
            Select mark should only be used with{' '}
            <Link href="/components/action-bar/design" isDesignCode>
              multi-selection mode
            </Link>
            . It’s a prominent indication that multiple items in a group can be
            selected at the same time.
          </Paragraph>
          <Paragraph>
            <img
              src={require('../../../imgs/diagrams/selectmark-multi-action.gif')}
            />
          </Paragraph>
          <Paragraph>
            The primary action should appear when the first item is selected.
            The state change and use of the Action color reinforce the
            relationship between the items and the action.
          </Paragraph>
          <DontDo
            dontText="use it in place of a checkbox group."
            doText="use it when multi-select is enabled."
            imgFilename="selectmark-multi"
          />

          <SectionSubhead>Complex Selectable Regions</SectionSubhead>
          <Paragraph>
            Selectable regions sometimes require sub-actions (buttons, links)
            and different states (expansion, activation), making use and design
            more difficult.
          </Paragraph>
          <Paragraph>
            Check our{' '}
            <Link href="/guidelines/content-selection" isDesignCodeLink>
              content selection patterns
            </Link>{' '}
            to see how select mark can be incorporated.
          </Paragraph>
        </Section>

        <Section title="Platform">
          <PlatformTable />
        </Section>

        <AccessibilityAuditComponentResultsSection componentName="Select Mark" />
      </PageWithSubNav>
    );
  }
}
